import { getAgeFromDOB } from "../Utils/GetAgeFromDOB";
import { getValue } from "../Utils/GetValue";
import { useCfmContext } from "../CfmContext";
import { use, useEffect, useMemo, useState } from "react";
import { annualise } from "../Utils/Annualise";
import isEqual from "lodash.isequal";

const MAX_TFLS_WITHDRAWAL = 268275;

const money = (v) =>
  typeof v === "number"
    ? v
    : parseFloat(String(v || "").replace(/[^0-9.-]+/g, "")) || 0;

const PensionCalculations = () => {
  const { state, dispatch } = useCfmContext();
  const [grossIncomeMap, setGrossIncomeMap] = useState({});

  let calculatedTfcAmount = "£0.00";

  const results = useMemo(() => {
    const results = [];
    if (!state?.details?.length) return [];

    const dropRiskMap = {};
    (state.dropRiskData || []).forEach((entry) => {
      if (entry.year && entry.rate) {
        dropRiskMap[parseInt(entry.year)] = parseFloat(entry.rate);
      }
    });

    state.details.forEach((person) => {
      const name = person.name;
      const currentAge = getAgeFromDOB(person.dob) ?? 18;
      const retirementAge = getValue(person.retirementAge);
      const inflationPct = state.inflation ? state.inflation : 0;

   

      // ---------- DOB-anchored month indexing ----------
      const [dobDD, dobMM, dobYYYY] = String(person.dob).split("/").map(Number);
      const dobAnchor = new Date(dobYYYY, (dobMM || 1) - 1, 1); // first of birth month

      const monthStart = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
      const diffMonths = (from, to) =>
        (to.getFullYear() - from.getFullYear()) * 12 +
        (to.getMonth() - from.getMonth());

      // convert YYYY string to month index relative to DOB (Jan of that year)
      const yearToMonthIndex = (yyyy) => {
        const yearNum = parseInt(yyyy, 10);
        if (isNaN(yearNum)) return null;
        const date = new Date(yearNum, 0, 1);
        return diffMonths(dobAnchor, date);
      };

      const startMonthIndex = Math.max(
        0,
        diffMonths(dobAnchor, monthStart(new Date()))
      );
      const endMonthIndex = 100 * 12 + 11; // last month before 101st birthday

      const ageStartIdx = (A) => A * 12;
      const ageEndIdx = (A) => A * 12 + 11;

      const monthsActiveInAge = (A) => {
        const s = ageStartIdx(A);
        const e = ageEndIdx(A);
        const lo = Math.max(s, startMonthIndex);
        const hi = Math.min(e, endMonthIndex);
        return Math.max(0, hi - lo + 1);
      };

      const fracForAge = (A) => Math.min(1, monthsActiveInAge(A) / 12);

      const personPensions =
        state.pensions?.filter((p) => p.name === name) || [];

      personPensions.forEach((pData) => {
           const fundData = [];
        const atrPct = parseFloat(pData?.atr) + parseFloat(inflationPct);

        const annualContrib =
          annualise({
            value: getValue(pData?.contribution?.value ?? 0),
            unit: pData?.contribution?.unit ?? "Annual",
          }) || 0;

        const fundStartValue = annualise({
          value: getValue(pData?.fundValue?.value ?? 0),
          unit: pData?.fundValue?.unit ?? "Annual",
        });

        const tfcEntry =
          (state?.drawdowns ?? []).find(
            (d) =>
              d.name.trim() === name.trim() &&
              d.type.trim() === pData.type.trim()
          ) || {};

        const rawTfcAge = parseInt(tfcEntry.age);
        const rawTfcPercentage = parseFloat(tfcEntry.percentage);
        const hasValidTfc =
          !isNaN(rawTfcAge) && !isNaN(rawTfcPercentage) && rawTfcPercentage > 0;

        const tflsAge = hasValidTfc ? rawTfcAge : null;
        const tflsPercentage = hasValidTfc ? rawTfcPercentage : 0;
        const phases = tfcEntry?.phases || [];
        const phaseAgeMap = {};
        const monthlyTfcMap = {};

        // ---------- Build monthly phased TFC map ----------
        if (phases.length) {
          const parseMMYYYY = (mmYYYY) => {
            const [mm, yyyy] = String(mmYYYY).split("/").map(Number);
            return new Date(yyyy, (mm || 1) - 1, 1);
          };
          const _addMonths = (d, n) =>
            new Date(d.getFullYear(), d.getMonth() + n, 1);
          const _diffMonths = (from, to) =>
            (to.getFullYear() - from.getFullYear()) * 12 +
            (to.getMonth() - from.getMonth());

          phases.forEach(({ start, end, amount }) => {
            const startDate = parseMMYYYY(start);
            const endDate = parseMMYYYY(end);
            const totalMonths = _diffMonths(startDate, endDate) + 1;
            const totalAmount = money(amount);
            if (totalMonths <= 0 || totalAmount <= 0) return;

            const monthlyTfc = totalAmount / totalMonths;

            for (let m = 0; m < totalMonths; m++) {
              const monthDate = _addMonths(startDate, m);
              const ageInMonths = diffMonths(dobAnchor, monthDate);
              if (ageInMonths < 0) continue;
              const ageYears = Math.floor(ageInMonths / 12);
              phaseAgeMap[ageYears] = (phaseAgeMap[ageYears] || 0) + monthlyTfc;
              monthlyTfcMap[ageYears] = monthlyTfcMap[ageYears]
                ? [...monthlyTfcMap[ageYears], monthlyTfc]
                : [monthlyTfc];
            }
          });
        }

        const ages = Array.from(
          { length: 101 - currentAge },
          (_, i) => currentAge + i
        );

        let fundValue = fundStartValue;
        let fundValueAtRetirement =
          currentAge === retirementAge ? fundValue : 0;
        let netFundAtRetirement =
          currentAge === retirementAge ? fundValue * (1 + atrPct / 100) : 0;

        const adhocEntries = (state.adHocData ?? []).filter(
          (a) =>
            a.name.trim() === name.trim() && a.type.trim() === pData.type.trim()
        );
        const adhocContributions = adhocEntries.filter(
          (x) => x.adHocType === "Contribution"
        );
        const adhocWithdrawals = adhocEntries.filter(
          (x) => x.adHocType === "Withdrawal"
        );

        const applyPhasedMonthlyDrawdown = (age, fundVal) => {
          const monthlyList = Array.isArray(monthlyTfcMap[age])
            ? monthlyTfcMap[age]
            : monthlyTfcMap[age]
            ? [monthlyTfcMap[age]]
            : [];
          let phasedMonthly = 0;
          for (const amt of monthlyList) {
            fundVal -= amt;
            phasedMonthly += amt;
            fundVal += fundVal * (atrPct / 100 / 12);
          }
          return { fundVal, phasedMonthly };
        };

        ages.forEach((age) => {
          let phasedMonthly = 0;
          const openingFund = fundValue;

          const mfrac = fracForAge(age);
          const isPartial = mfrac > 0 && mfrac < 1;

          // Regular income (already age-based) – pro-rate if partial
          const baseRegularIncome = (state.regularIncome || [])
            .filter(
              (r) =>
                r.name === name &&
                +r.start <= age &&
                +r.end >= age &&
                pData.type.trim() === r.type.trim()
            )
            .reduce((sum, r) => sum + money(r.amount), 0);

          const totalRegularIncome = isPartial
            ? baseRegularIncome * mfrac
            : baseRegularIncome;
          fundValue -= totalRegularIncome;

          // ---------- Birthday-year ad-hoc split (key change) ----------
          const ageStart = ageStartIdx(age);
          const ageEnd = ageEndIdx(age);
          const currentGrowthRate = atrPct;

          const appliedAdhocContribution = adhocContributions.reduce(
            (sum, item) => {
              const mIndex = yearToMonthIndex(item.year);
              if (mIndex == null) return sum;
              const start = mIndex; // Jan of that calendar year
              const end = mIndex + 11; // Dec of that calendar year
              const overlap = Math.max(
                0,
                Math.min(ageEnd, end) - Math.max(ageStart, start) + 1
              );
              if (overlap > 0) {
                const amt =
                  money(item.amount?.value ?? 0) *
                  ((100 - currentGrowthRate) / 100);
                return sum + amt * (overlap / 12);
              }
              return sum;
            },
            0
          );

          const appliedAdhocWithdrawal = adhocWithdrawals.reduce(
            (sum, item) => {
              const mIndex = yearToMonthIndex(item.year);
              if (mIndex == null) return sum;
              const start = mIndex;
              const end = mIndex + 11;
              const overlap = Math.max(
                0,
                Math.min(ageEnd, end) - Math.max(ageStart, start) + 1
              );
              if (overlap > 0) {
                const amt =
                  money(item.amount?.value ?? 0) *
                  ((100 - currentGrowthRate) / 100);
                return sum + amt * (overlap / 12);
              }
              return sum;
            },
            0
          );

          // Inflation-adjusted contribution (pre-retirement), pro-rate if partial
          const yearsFromNow = age - currentAge;
          const inflationFactor = Math.pow(
            1 + inflationPct / 100,
            yearsFromNow
          );
          const baseContribution =
            age < retirementAge ? annualContrib * inflationFactor : 0;
          const contribution = isPartial
            ? baseContribution * mfrac
            : baseContribution;

          // Phased TFC (monthly)
          if (phaseAgeMap[age]) {
            const result = applyPhasedMonthlyDrawdown(age, fundValue);
            fundValue = result.fundVal;
            phasedMonthly = result.phasedMonthly;
          }

          // Apply flows
          fundValue += contribution + appliedAdhocContribution;
          fundValue -= appliedAdhocWithdrawal;

          // Growth (pro-rated if partial)
          if (fundValue > 0) {
            const growthFactor = isPartial
              ? (atrPct / 100) * mfrac
              : atrPct / 100;
            fundValue += fundValue * growthFactor;
          }

          // One-off TFC at tflsAge
          let tflsWithdrawal = 0;
          if (age === tflsAge) {
            const base = fundValue;
            const tflsAmount = Math.min(
              base * (tflsPercentage / 100),
              MAX_TFLS_WITHDRAWAL
            );
            tflsWithdrawal = tflsAmount;
            fundValue -= tflsWithdrawal;
            calculatedTfcAmount = `£${tflsAmount.toFixed(2)}`;
          }

          // Retirement snapshots
          if (age === retirementAge - 1) {
            fundValueAtRetirement = fundValue;
          } else if (age === retirementAge) {
            netFundAtRetirement = fundValueAtRetirement;
            fundValue = netFundAtRetirement;
          }

          // Drop risk by calendar year (kept as-is; change if you also want this birthday-aligned)
          const calendarYear = new Date().getFullYear() + (age - currentAge);
          const dropRiskPct = dropRiskMap[calendarYear];
          if (dropRiskPct) {
            fundValue *= 1 - dropRiskPct / 100;
          }

          // Taxed drawdowns (age-ranged)
          // - If unit is %, take that % of the (already-updated) fund this age.
          // - If unit is a cash amount, uprate it by inflation from currentAge -> age.
          //   Then pro-rate for partial ages (mfrac).

          const inflationFactorTD = Math.pow(
            1 + inflationPct / 100,
            yearsFromNow
          );

          const totalTaxedDrawdownBase = (pData?.drawdowns ?? [])
            .filter(
              ({ start, end }) => age >= parseInt(start) && age <= parseInt(end)
            )
            .reduce((acc, { value, unit }) => {
              const drawdownValue = money(value ?? 0);
              const isPercent = String(unit || "")
                .toLowerCase()
                .includes("%");

              const amount = isPercent
                ? (drawdownValue / 100) * fundValue // % of fund (no extra inflation)
                : drawdownValue * inflationFactorTD; // cash amount uprated by inflation

              return acc + amount;
            }, 0);

          const totalTaxedDrawdown = isPartial
            ? totalTaxedDrawdownBase * mfrac // pro-rate for partial first/last ages
            : totalTaxedDrawdownBase;

          fundValue -= totalTaxedDrawdown;

          const safeFundValue = Math.max(0, fundValue);

          // ---------- Output row ----------
          const openingForDisplay =
            age >= retirementAge
              ? safeFundValue + totalTaxedDrawdown
              : openingFund;

          fundData.push({
            age,
            dropRiskApplied: dropRiskPct != null ? `${dropRiskPct}%` : "",
            fundValue: `£${
              age >= retirementAge
                ? safeFundValue > 0
                  ? (safeFundValue + totalTaxedDrawdown).toFixed(2)
                  : "0.00"
                : openingForDisplay > 0
                ? Number(openingForDisplay).toFixed(2)
                : "0.00"
            }`,
            contribution: `£${contribution.toFixed(2)}`,
            adhocContribution: `£${(
              appliedAdhocContribution *
              (100 / (100 - currentGrowthRate))
            ).toFixed(2)}`,
            adhocWithdrawal: `£${(
              appliedAdhocWithdrawal *
              (100 / (100 - currentGrowthRate))
            ).toFixed(2)}`,
            taxedDrawdown: `£${
              safeFundValue > 0 ? totalTaxedDrawdown.toFixed(2) : "0.00"
            }`,
            tflsWithdrawal:
              age === tflsAge ? `£${tflsWithdrawal.toFixed(2)}` : "£0.00",
            grossRegularIncome: `£0.00`,
            fourPercentRule: `£0.00`,
            netFund: `£${(
              fundValue +
              contribution +
              appliedAdhocContribution -
              appliedAdhocWithdrawal
            ).toFixed(2)}`,
            potentialDropRisk: atrPct,
            phasedTfc: phaseAgeMap[age]
              ? `£${phasedMonthly.toFixed(2)}`
              : "£0.00",
            regularIncome: `£${totalRegularIncome.toFixed(2)}`,
          });
        });

        results.push({
          name,
          type: pData.type.trim(),
          pensionFunds: fundData,
        });
      });
    });

    return results;
  }, [state]);

  useEffect(() => {
    const existing = state.pensionResults || [];

    const personAwareResults = (state.details || [])
      .map((person) => {
        const byPerson = (results || []).filter(
          (r) => r.name.trim() === person.name.trim()
        );
        if (byPerson.length === 0) {
          return [{ name: person.name.trim(), type: "", pensionFunds: [] }];
        }
        return byPerson;
      })
      .flat();

    const isDifferent = !isEqual(existing, personAwareResults);
    if (isDifferent) {
      dispatch({ type: "SET_PENSION_RESULTS", payload: personAwareResults });
    }
  }, [results, state.details, state.pensionResults, dispatch]);

  return results;
};

export default PensionCalculations;
